/**
 * Сворачиваемая панель слева и ручка на её шве.
 *
 * Панель толкает содержимое, а не накрывает его: анимируется ширина, соседняя
 * область сужается. Готового блока под такой сайдбар в Application UI нет — все
 * Sidebar Layouts построены вокруг навигационного меню, которого в игре нет,
 * поэтому он собран утилитами.
 *
 * Компонент общий для игры и стенда графики: разметка шва, ручка и переходы
 * должны совпадать, иначе две страницы разъедутся на первой же правке.
 */

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid'
import type { ReactNode } from 'react'
import { store, stored } from './app.socket.js'

/** Ширина боковой панели: Tailwind `w-80`. */
export const PANEL = 320

const SIDEBAR_TOGGLE_KEY = 'sidebar-toggle'

const readSidebarToggle = (): boolean => stored(SIDEBAR_TOGGLE_KEY) === '1'

function resolveSidebarAvailability(): boolean {
  const params = new URLSearchParams(window.location.search)
  if (!params.has('sidebar')) return readSidebarToggle()

  const value = params.get('sidebar')
  const enabled = value === null || value === '' ? !readSidebarToggle() : value === '1'
  store(SIDEBAR_TOGGLE_KEY, enabled ? '1' : '0')

  // Без значения `sidebar` — одноразовая команда, поэтому убираем её,
  // чтобы обновление страницы не переключило состояние обратно.
  if (value === null || value === '') {
    params.delete('sidebar')
    const query = params.toString()
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    )
  }

  return enabled
}

/** Состояние доступности сайдбаров для обеих страниц приложения. */
export const SIDEBAR_AVAILABLE = resolveSidebarAvailability()

interface SidebarProps {
  /** Панель и ручка полностью убраны со страницы. */
  available?: boolean
  open: boolean
  onToggle: () => void
  /**
   * Первый кадр без перехода: панель успевает встать в своё положение до того,
   * как включится анимация ширины, и страница не открывается рывком.
   */
  animate?: boolean
  children: ReactNode
}

export function Sidebar({ available = true, open, onToggle, animate = true, children }: SidebarProps) {
  const glide = animate ? 'transition-[width] duration-200 ease-in-out' : ''
  const slide = animate ? 'transition-[left] duration-200 ease-in-out' : ''

  if (!available) return null

  return (
    <>
      <aside className={`relative z-10 shrink-0 overflow-hidden ${glide} ${open ? 'w-80' : 'w-0'}`}>
        <div className="flex h-full w-80 flex-col overflow-hidden border-r border-gray-200 bg-white dark:border-white/10 dark:bg-gray-800">
          {children}
        </div>
      </aside>

      {/* Ручка на шве: центр на 25 % высоты окна, считая снизу. */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={open ? 'Hide panel' : 'Show panel'}
        className={`fixed top-[75%] z-20 -ml-px flex h-12 w-3.5 cursor-pointer -translate-y-1/2 items-center justify-center rounded-r-md border-y border-r border-gray-200 bg-white text-gray-500 hover:text-gray-900 dark:border-white/10 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-white ${slide} ${open ? 'left-80' : 'left-0'}`}
      >
        {open ? (
          <ChevronLeftIcon aria-hidden="true" className="size-3" />
        ) : (
          <ChevronRightIcon aria-hidden="true" className="size-3" />
        )}
      </button>
    </>
  )
}
